import React from "react";

import { OrderDto, OrderStatusDto } from "services/orders/restModels";
import { formatDateTime } from "shared/lib/dateTime";
import {
    orderDeliveryTypeLabel,
    orderPaymentTypeLabel,
    orderStatusLabel,
} from "shared/lib/orderLabels";
import { Button, SectionCard } from "shared/ui";

import { useOrderDetailsContext } from "features/OrderDetails/context";
import { deliveryDetailsRows } from "features/OrderDetails/lib/deliveryDetailsRows";

import { Actions, Label, Links, PaidValue, ReceiptLink, Row, Rows, Value } from "./styles";

const paymentTone = (order: OrderDto): "paid" | "unpaid" | "refunded" | null => {
    if (order.status === OrderStatusDto.canceledWithRefund) return "refunded";
    if (!order.paymentDetails.isOnline) return null;

    return order.paymentDetails.isPaid ? "paid" : "unpaid";
};

const PAID_LABELS = {
    paid: "Оплачен",
    unpaid: "Не оплачен",
    refunded: "Возврат",
};

export const OrderSummary = ({ order }: { order: OrderDto }) => {
    const state = useOrderDetailsContext();

    const tone = paymentTone(order);

    const rows = [
        { label: "Дата заказа", value: formatDateTime(order.createdAt) },
        { label: "Сумма заказа", value: state.formatPrice(order.totalPrice) },
        { label: "Статус заказа", value: orderStatusLabel(order.status) },
        { label: "Способ получения", value: orderDeliveryTypeLabel(order.deliveryDetails.type) },
        { label: "Тип оплаты", value: orderPaymentTypeLabel(order.paymentDetails.type) },
        ...deliveryDetailsRows(order.deliveryDetails, state.locale),
        { label: "Комментарий к заказу", value: order.comment },
    ].filter((row) => row.value !== "" && row.value !== null && row.value !== undefined);

    return (
        <SectionCard title={`Заказ №${order.id}`}>
            <Rows>
                {rows.map((row) => (
                    <Row key={row.label}>
                        <Label>{row.label}</Label>
                        <Value>{row.value}</Value>
                    </Row>
                ))}
                {tone !== null && (
                    <Row>
                        <Label>Статус оплаты</Label>
                        <PaidValue $tone={tone}>{PAID_LABELS[tone]}</PaidValue>
                    </Row>
                )}
            </Rows>

            {(order.paymentDetails.receiptLink || order.paymentDetails.refundReceiptLink) && (
                <Links>
                    {order.paymentDetails.receiptLink && (
                        <ReceiptLink
                            href={order.paymentDetails.receiptLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Чек оплаты
                        </ReceiptLink>
                    )}
                    {order.paymentDetails.refundReceiptLink && (
                        <ReceiptLink
                            href={order.paymentDetails.refundReceiptLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Чек возврата
                        </ReceiptLink>
                    )}
                </Links>
            )}

            {/* Возможность возврата считает бэк (canRefund) — кабинет её не выводит сам. */}
            {order.paymentDetails.canRefund && (
                <Actions>
                    <Button
                        variant="outlined"
                        color="danger"
                        disabled={state.refunding}
                        onClick={state.openRefundDialog}
                    >
                        Вернуть деньги
                    </Button>
                </Actions>
            )}
        </SectionCard>
    );
};
